import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useDispatch } from 'react-redux';
import BuzzInput from '../../../components/BuzzInput/BuzzInput';
import useToast from '../../../hooks/useToast';
import { updateFlagRelease } from '../../Dashboard/FeatureManagement/featureFlagsSlice';
import {
  releaseColour,
  severityColour,
} from '../../Dashboard/FeatureManagement/flagDisplay';
import { isProductionEnv } from '../../../environments';
import { findWithAncestors, thunkErrorMessage } from './useFlagTree';

const ROOT_KEY = 'website_builder';

/**
 * Every `website_builder.*` flag with an all-schools ON/OFF switch.
 *
 * The switch only ever moves a flag between ALL and OFF, sending `releaseStatus` and
 * `reason` alone: the backend leaves omitted fields untouched, so a PARTIAL pilot list or
 * canary percentage survives an OFF → ALL round trip. PARTIAL / CANARY flags are not
 * switchable here — flattening them to ALL or OFF is a rollout decision for Feature
 * Management.
 */

const labelOf = (key) =>
  key === ROOT_KEY ? ROOT_KEY : key.slice(ROOT_KEY.length + 1);

/** Tree → rows in depth-first order, siblings sorted by key. */
const flatten = (nodes, depth = 0, out = []) => {
  [...(nodes || [])]
    .sort((a, b) => a.key.localeCompare(b.key))
    .forEach((treeNode) => {
      out.push({ treeNode, depth });
      flatten(treeNode.children, depth + 1, out);
    });
  return out;
};

const countDescendants = (treeNode) =>
  (treeNode.children || []).reduce(
    (total, child) => total + 1 + countDescendants(child),
    0
  );

const chipStyle = ({ bg, fg }) => ({
  backgroundColor: bg,
  color: fg,
  fontWeight: 500,
  marginRight: 4,
});

const releaseLabel = (row) => {
  switch (row.releaseStatus) {
    case 'PARTIAL': {
      const count = (row.schoolIds || []).length;
      return `${count} school${count === 1 ? '' : 's'}`;
    }
    case 'CANARY':
      return `${row.rolloutPct || 0}%`;
    default:
      return row.releaseStatus || 'OFF';
  }
};

// All-schools switches are a production release decision; there they belong to Feature
// Management, which shows the full release state and log.
const READ_ONLY = isProductionEnv();

/** → null when the switch can be used, otherwise the tooltip saying why not. */
const lockedReason = (row) => {
  if (READ_ONLY) {
    return 'Read-only on production. Use Dashboard → Feature Management.';
  }
  if (row.availability && row.availability !== 'LIVE') {
    return `${row.availability}: off for every school whatever the release. Restore it in Dashboard → Feature Management.`;
  }
  if (row.releaseStatus === 'PARTIAL' || row.releaseStatus === 'CANARY') {
    return 'Partially rolled out. Manage in Dashboard → Feature Management.';
  }
  return null;
};

// `email`, `tree` and `reload` come from the page's shared useFlagTree.
const WebsiteBuilderFeatures = ({ email, tree, reload }) => {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast, WarningToast } = useToast();

  const [pending, setPending] = useState(null); // { treeNode, target, seen }
  const [reason, setReason] = useState('');
  const [busyKey, setBusyKey] = useState(null);

  const found = tree ? findWithAncestors(tree, ROOT_KEY) : null;
  const rows = found ? flatten([found.node]) : [];

  const openConfirm = (treeNode) => {
    if (READ_ONLY) return;
    const row = treeNode.node.row || {};
    setReason('');
    setPending({
      treeNode,
      target: row.releaseStatus === 'ALL' ? 'OFF' : 'ALL',
      // What the switch showed when clicked; the write aborts if this has moved.
      seen: {
        releaseStatus: row.releaseStatus,
        availability: row.availability,
      },
    });
  };

  const closeConfirm = () => setPending(null);

  const handleConfirm = () => {
    // Guarded here too, not only by the disabled switch: this is the path that writes.
    if (READ_ONLY || !pending) return;
    const { treeNode, target, seen } = pending;
    const { key } = treeNode;
    const label = labelOf(key);
    const turned = target === 'ALL' ? 'ON' : 'OFF';
    const fullReason = `${
      reason.trim() ||
      `Turned ${turned} for all schools${email ? ` by ${email}` : ''}`
    } (Teenybeans Portal → Website)`;

    setPending(null);
    setBusyKey(key);

    // Re-read right before writing, so a flag someone has since moved to PARTIAL (or
    // killed) is not flattened back to ALL / OFF from a stale screen.
    reload()
      .catch((body) => {
        throw new Error(
          thunkErrorMessage(body, 'Could not load feature flags')
        );
      })
      .then((freshTree) => {
        const fresh = findWithAncestors(freshTree, key);
        const row = fresh && fresh.node.node.row;
        if (
          !row ||
          row.releaseStatus !== seen.releaseStatus ||
          row.availability !== seen.availability
        ) {
          const now = !row
            ? 'missing'
            : row.availability !== 'LIVE'
            ? row.availability
            : row.releaseStatus;
          WarningToast(
            `${label} changed since the page loaded (now ${now}). Nothing was changed.`
          );
          return null;
        }

        return dispatch(
          updateFlagRelease({
            key,
            data: { releaseStatus: target, reason: fullReason },
          })
        )
          .unwrap()
          .catch((body) => {
            throw new Error(
              thunkErrorMessage(body, 'Could not update the feature flag')
            );
          })
          .then(() => {
            SuccessToast(`${label} turned ${turned} for all schools.`);
            return reload();
          });
      })
      // Errors raised above are Errors; a failed post-write reload rejects with a raw body.
      .catch((err) =>
        ErrorToast(
          err instanceof Error
            ? err.message
            : thunkErrorMessage(err, 'Could not reload feature flags')
        )
      )
      .finally(() => setBusyKey(null));
  };

  const renderSwitch = (treeNode) => {
    const row = treeNode.node.row || {};
    if (busyKey === treeNode.key) return <CircularProgress size={24} />;

    const locked = lockedReason(row);
    const control = (
      <Switch
        color="primary"
        checked={row.releaseStatus === 'ALL'}
        // One write at a time: each one reloads the tree the others are compared against.
        disabled={!!locked || !!busyKey}
        onChange={() => openConfirm(treeNode)}
        inputProps={{ 'aria-label': `Toggle ${treeNode.key}` }}
      />
    );
    // A disabled control fires no mouse events, so the tooltip needs a wrapper.
    return locked ? (
      <Tooltip title={locked}>
        <span>{control}</span>
      </Tooltip>
    ) : (
      control
    );
  };

  const pendingRow = pending ? pending.treeNode.node : null;
  const pendingDescription =
    pendingRow &&
    ((pendingRow.meta && pendingRow.meta.description) ||
      (pendingRow.row && pendingRow.row.notes));
  const pendingChildren = pending ? countDescendants(pending.treeNode) : 0;
  const turningOff = pending && pending.target === 'OFF';

  return (
    <Box>
      <Typography variant="h6" component="div">
        <Box fontWeight="fontWeightMedium">Website Builder features</Box>
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Each switch turns a feature on or off for all schools. Pilots and
        canaries are managed in Dashboard → Feature Management.
      </Typography>

      {READ_ONLY && (
        <Box mt={2}>
          <Alert severity="info">
            Production: feature toggles are read-only here.
          </Alert>
        </Box>
      )}
      {!tree && (
        <Box mt={3}>
          <CircularProgress size={24} />
        </Box>
      )}
      {tree && !found && (
        <Box mt={3}>
          <Typography variant="body2" color="textSecondary">
            No website_builder flags found.
          </Typography>
        </Box>
      )}
      {found && (
        <Box mt={2}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Feature</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">On / Off</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ treeNode, depth }) => {
                const view = treeNode.node;
                const row = view.row || {};
                const isContainer = row.kind === 'CONTAINER';
                const description =
                  (view.meta && view.meta.description) || row.notes || '—';
                return (
                  <TableRow key={treeNode.key}>
                    <TableCell>
                      <Box pl={depth * 3}>
                        <Typography
                          variant="body2"
                          style={{
                            fontFamily: 'monospace',
                            fontWeight: isContainer ? 600 : 400,
                          }}>
                          {labelOf(treeNode.key)}
                        </Typography>
                        {isContainer && (
                          <Typography variant="caption" color="textSecondary">
                            Controls every feature below.
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={releaseLabel(row)}
                          style={chipStyle(releaseColour(row.releaseStatus))}
                        />
                        {row.availability && row.availability !== 'LIVE' && (
                          <Chip
                            size="small"
                            label={row.availability}
                            style={chipStyle(severityColour('error'))}
                          />
                        )}
                      </Box>
                      {treeNode.blockedByAncestor && (
                        <Typography variant="caption" color="textSecondary">
                          Blocked by {labelOf(treeNode.blockedByAncestor)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {isContainer ? null : renderSwitch(treeNode)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={!!pending} onClose={closeConfirm} maxWidth="sm" fullWidth>
        {pending && (
          <>
            <DialogTitle>
              Turn {turningOff ? 'OFF' : 'ON'} {labelOf(pending.treeNode.key)}{' '}
              for ALL schools?
            </DialogTitle>
            <DialogContent dividers>
              {pendingDescription && (
                <Typography variant="body2" color="textSecondary">
                  {pendingDescription}
                </Typography>
              )}
              {turningOff && pendingChildren > 0 && (
                <Box mt={2}>
                  <Alert severity="warning">
                    This also hides the {pendingChildren} feature
                    {pendingChildren === 1 ? '' : 's'} beneath it.
                  </Alert>
                </Box>
              )}
              <BuzzInput
                id="tb-website-feature-reason"
                name="reason"
                label="Reason (optional, recorded in the release log)"
                mt={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={closeConfirm}>Cancel</Button>
              <Button
                onClick={handleConfirm}
                variant="contained"
                color={turningOff ? 'secondary' : 'primary'}>
                Turn {turningOff ? 'OFF' : 'ON'} for all schools
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default WebsiteBuilderFeatures;
